import _ from 'lodash'
import moment from 'moment'
import Errors from '../../utils/Errors'
import {
    getStatusString,
    getStepStatus,
    getAlertHabFunctions,
    getAlertsHabFunctions,
    getAlertButtonsForInvolvement,
    getStepTkey,
    getActiveStep,
    getUserInvolvementForAlert,
    getAlertIdsForUserStaticWorkflow,
    workflowManager
} from './workflow'
import { basicContext } from '../../utils/contextUtils'
import {
    changeFieldRequired,
    setFieldEdition,
    setFieldListOptions,
    setFieldVisibility
} from '../../../apps/KpModule/actions'
import { GET_OBJECT_SUCCESS } from '../../../apps/KpModule/actions/api'
import { buildAlertPeriodQuery } from './utils/alertPeriodQuery'

const twoMonthEarlierISO = () => {
    return moment()
        .subtract(2, 'months')
        .format('YYYY-MM-DD')
}

const todayISO = () => moment().format('YYYY-MM-DD')

async function getWorkflowModel(alertConfigurationId, context) {
    let workflowModel
    try {

        workflowModel = await global.app.S.WorkflowConfigsByAlert.get(
            {alertConfiguration: new global.ObjectID(alertConfigurationId)},
            {
                group: context.group,
                fieldPath: [
                    'id',
                    'filesRequired',
                ]
            }
        )
    } catch {
        console.log(`workflowModel not found for alertConf : ${alertConfigurationId}`)
    }

   return workflowModel
}

async function findAlertProcessings(context) {

    const datesQuery = buildAlertPeriodQuery(
        _.get(context, 'data.period', [twoMonthEarlierISO(), todayISO()])
    )

    let activeQuery
    switch (_.get(context, 'data.workflowFilter.id')) {
        case 'alertToProcess':
            activeQuery = { active: true }
            break
        case 'alertInProgress':
            activeQuery = { step: { $nin: ['risk', 'noRisk'] } }
            break
        case 'closedAlertRisk':
            activeQuery = { step: 'risk' }
            break
        case 'closedAlertNoRisk':
            activeQuery = { step: 'noRisk' }
            break
        default:
            activeQuery = {}
    }

    const alertIds = await getAlertIdsForUserStaticWorkflow(context, {
        ...datesQuery,
        ...activeQuery
    })

    const alerts = await global.app.S.Alert.find({
        ...basicContext(context),
        query: { _id: { $in: alertIds } },
        fieldPath: [
            'id',
            'alertConfiguration.id',
            'store.name',
            'store.fullName',
            'organizations.name',
            'organizations.organizationAxis.useInScope',
            'mesh',
            'values',
            'reason.id',
            'reason.tName',
            'dataPeriod',
            'details',
            'comments.id',
            'comments.text',
            'comments.date',
            'lastComment',
            'alertCreationTime',
            'closingDate',
            'workflow.step',
            'workflow.order',
        ]
    })

    const habFunctionsByAlert = await getAlertsHabFunctions(alerts, context)

    // many alerts share the same few alert configurations: fetch each one only once
    const alertConfigurationIds = _.uniq(alerts.map(alert => alert.alertConfiguration.id))
    const alertConfigurations = await Promise.all(
        alertConfigurationIds.map(id => global.app.S.AlertConfiguration.get(id, {
            group: context.group,
            fieldPath: ["name", "themeNames"],
        }))
    )
    const alertConfigurationsById = _.zipObject(alertConfigurationIds, alertConfigurations)

    // the alert processing id is the alert id, to help the save operation
    return alerts.map(alert => {
        const statusHabFunctions = habFunctionsByAlert.get(alert.id)
        const alertConf = alertConfigurationsById[alert.alertConfiguration.id]

        const currentStep = _.get(alert, 'workflow.step')
        const sortedComments = alert.comments.sort((a, b) => new Date(b.date) - new Date(a.date));
        const lastComment = sortedComments[0]
        return {
            ..._.pick(alert, [
                'id',
                'reason',
                'store',
                'lastComment',
                'dataPeriod',
                'mesh',
                'values',
                'details',
                'alertCreationTime',
                'closingDate',
            ]),
            comment:lastComment && lastComment.text,
            organizations: alert.organizations.filter(org => org.organizationAxis.useInScope),
            alertConfigurationName: context.translateName(
                alertConf.name
            ),
            themeNames: alertConf.themeNames,
            statusString: getStatusString(alert, context),
            stepStatus: getStepStatus(alert),
            disableGroupedAction: ['pending', 'risk', 'noRisk'].includes(currentStep),
            statusHabFunctions
        }
    })
}

async function getAlertProcessing(alertId, context) {
    const requireActive = _.get(context, 'data.workflowFilter.id') === 'alertToProcess'

    // ignore not found is used to get nothing after action execution
    const alert = await global.app.S.Alert.get(alertId, {
        ...basicContext(context),
        ignoreNotFound: context.ignoreNotFound,
        fieldPath: [
            'id',
            'alertConfiguration.id',
            'alertConfiguration.reason.id',
            'alertConfiguration.themeNames',
            'alertConfiguration.alertDescriptions.language.id',
            'alertConfiguration.alertDescriptions.description',
            'organizations.id',
            'organizations.name',
            'organizations.organizationAxis.useInScope',
            'mesh',
            'lastComment',
            'alertCreationTime',
            'closingDate',
            'store.id',
            'store.fullName',
            'values',
            'reason.id',
            'reason.tName',
            'details',
            'dataPeriod',
            'keyAndValues',
            'ticketsLines',
            'files.id',
            'files.filename',
            'files.date',
            'files.user',
            'comments.id',
            'comments.text',
            'comments.date',
            'comments.user',
            'workflow.step',
            'workflow.order'
        ]
    })
    if (!alert) return null

    const involvement = await getUserInvolvementForAlert(alert, context)
    if (!involvement || (requireActive && !involvement.active)) return null

    const alertConfiguration = alert.alertConfiguration || {}

    // do the asynchronous calls in parallel
    const [workflowModel, currentStep, statusHabFunctions] = await Promise.all([
        getWorkflowModel(alertConfiguration.id, context),
        getActiveStep(alert, context),
        getAlertHabFunctions(alert, context)
    ])

    const buttons = getAlertButtonsForInvolvement(involvement)

    // this is the list of ids that will be proposed on the reason dropDown
    const alertConfigurationReasonsIds = alertConfiguration.reason
        .filter(reason => {
            return !reason.automatic || reason.id === _.get(alert, 'reason.id')
        })
        .map(reason => reason.id)


    const alertDescription = alertConfiguration.alertDescriptions?.find(alertDescription => {
        return _.get(alertDescription, 'language.id') === _.get(context, 'language.id')
    })

    return {
        ..._.pick(alert, [
            'id',
            'store',
            'dataPeriod',
            'mesh',
            'values',
            'reason',
            'alertCreationTime',
            'closingDate',
            'lastComment',
            'details',
            'keyAndValues',
            'ticketsLines'
        ]),
        organizations: alert.organizations.filter(org => org.organizationAxis.useInScope),
        isManager: ['review', 'pending'].includes(currentStep),
        alertConfigurationName: context.translateName(alertConfiguration.name),
        description: alertDescription?.description,
        themeNames: alertConfiguration.themeNames,
        alertConfigurationReasonsIds,
        files: alert.files.map(
            _.partialRight(_.pick, ['id', 'filename', 'date', 'user'])
        ),
        filesRequired: !!workflowModel?.filesRequired,
        comments: alert.comments.map(
            _.partialRight(_.pick, ['id', 'text', 'date', 'user'])
        ),
        stepStatus: getStepStatus(alert),
        statusString: getStatusString(alert, context),
        statusHabFunctions,
        buttons
    }
}

async function saveAlertProcessing(alertProcessing = {}, context = {}) {
    // don't save anything without the basic data
    if (!context.action || !alertProcessing.id) return alertProcessing
    if (!alertProcessing.reason) throw new Errors.ValidationError('reasonIsMandatory')

    // alertProcessing id is an alert id
    const alert = await global.app.S.Alert.get(alertProcessing.id, {
        ...basicContext(context),
        fieldPath: ['id', 'workflow.step', 'workflow.order', 'comments.id']
    })
    const reason = await global.app.S.Reason.get(alertProcessing.reason.id, {
        ...basicContext(context),
        fieldPath: ['id', 'name', 'automatic']
    })
    const currentStatus = _.pick(alert.workflow, ['step', 'order'])

    const user = context.user && {
        id: context.user.id,
        name: context.user.name
    }
    const date = moment().format('YYYY-MM-DD HH:mm')
    const commentTexts = [
        `${context.tc(context.action)} ${context.tc(
            getStepTkey(currentStatus.step)
        )}`,
        context.translateName(reason.name)
    ]

    const userComments = _(alertProcessing.comments)
        .filter(comment => !alert.comments.some(oldComment => oldComment.id === comment.id))
        .orderBy('date', 'desc')
        .value()

    const lastCommentObject = userComments[0]

    const lastCommentQuery = lastCommentObject ? {lastComment: lastCommentObject.text} : {}

    const comments = [
        ...(alertProcessing.comments || []),
        ...commentTexts.map(text => ({ user, date, text }))
    ]

    // use the platform to validate and save alert fields that can change
    await global.app.S.Alert.save(
        {
            id: alert.id,
            ..._.pick(alertProcessing, ['reason', 'details', 'files']),
            comments
        },
        {
            ...basicContext(context),
            action: context.action,
            fieldPath: [
                'id',
                'reason.id',
                'reason.automatic',
                'details',
                'comments.id',
                'comments.text',
                'comments.date',
                'comments.user',
                'files.id',
                'files.filename',
                'files.date',
                'files.user'
            ]
        }
    )

    const nextStatus = workflowManager.getNextStatus(
        currentStatus,
        context.action,
        alert.workflow.maxOrders
    )

    const isClosingAction = ['risk', 'noRisk'].includes(nextStatus.step)

    const historyEntry = {
        date: new Date(),
        action: context.action,
        status: {
            step: nextStatus.step,
            order: nextStatus.order
        },
        user,
        reason: isClosingAction ? new global.ObjectID(reason.id) : null,
        automatic: isClosingAction ? reason.automatic : false
    }

    const closingDateSetQuery = isClosingAction ? {closingDate: new Date()} : {}

    // directly set the new status on the alert collection
    const alertCollection = global.app.S.Alert.collection
    await alertCollection.updateOne(
        { _id: new global.ObjectID(alertProcessing.id) },
        {
            $set: {
                'workflow.step': nextStatus.step,
                'workflow.order': nextStatus.order,
                'workflow.lastUpdate': new Date(),
                ...lastCommentQuery,
                ...closingDateSetQuery
            },
            $push: {
                'workflow.processingHistory': historyEntry
            }
        }
    )

    return await getAlertProcessing(alertProcessing.id, {
        ...context,
        ignoreNotFound: true
    })
}

export const entities = [
    {
        name: 'StaticWorkflow',
        fields: [
            'step',
            { path: 'order', type: 'integer' },
            { path: 'active', type: 'boolean' },
            { type: 'Alert', index: true },
            { type: 'AlertConfiguration', index: true },
            'HabFunction',
            { path: 'date.gte', type: 'date' },
            { path: 'date.lt', type: 'date' }
        ]
    },
    {
        name: 'AlertProcessing',
        type: null,
        facets: ['comments', 'files'],
        fields: [
            {type: 'Reason', nullable: true}
        ],
        find: function(context, callback) {
            this.prepareContext(context, 'L')
                .then(context => findAlertProcessings(context))
                .then(objects => callback(null, objects))
                .catch(error => callback(error))
        },
        get: function(id, context, callback) {
            this.prepareContext(context, 'R')
                .then(context => getAlertProcessing(id, context))
                .then(object => callback(null, object))
                .catch(error => callback(error))
        },
        save: function(newObject, context, callback) {
            this.prepareContext(context, 'S')
                .then(context => saveAlertProcessing(newObject, context))
                .then(object => callback(null, object))
                .catch(error => callback(error))
        }
    }
]

export const module_ = {
    object: 'AlertProcessing',
    tKey: 'mTitle_alertProcessing',
    category: 'tracking',
    defaultSortBy: 'alertConfigurationName',
    defaultSortDirection: 'ASC',
    newable: false,
    removable: false,
    actions: [
        {method: 'PUT', name: 'validate', tKey: 'validate', accessIdPath: 'accessId'},
        {method: 'PUT', name: 'question', tKey: 'question', accessIdPath: 'accessId'},
        {method: 'PUT', name: 'refuse', tKey: 'refuse', accessIdPath: 'accessId'},
    ],
    viewMap: {
        dt: [
            { path: 'alertConfigurationName', tKey: 'alert', width: 200 },
            {
                path: 'dataPeriod',
                tKey: 'period',
                width: 100,
                initiallyNotVisible: true
            },
            { path: 'organizations', type: 'objectArray', display: 'name', initiallyNotVisible: true},
            { path: 'store', type: 'object', display: 'fullName', initiallyNotVisible: true },
            { path: 'mesh', tKey: 'for', width: 320 },
            { path: "themeNames", tKey: "indicator_plural", initiallyNotVisible: true },
            { path: 'values', width: 100 },
            { path: 'alertCreationTime', tKey: 'detectionDate', type: 'dateTimeString', initiallyNotVisible: true },
            { path: 'reason', display: 'tName', width: 230 },
            { path: 'details', initiallyNotVisible: true},
            //{ path: 'comment', initiallyNotVisible: true},
            { path: 'stepStatus', tKey: 'step', initiallyNotVisible: true },
            {
                path: 'statusString',
                tKey: 'status',
                translateName: true,
                width: 100,
                initiallyNotVisible: true
            },
            {
                path: 'statusHabFunctions',
                tKey: 'functions',
                initiallyNotVisible: true
            },
            { path: 'closingDate', type: 'dateTimeString', initiallyNotVisible: true },
            { path: 'lastComment', tKey: 'lastUserComment', initiallyNotVisible: true },
            { path: 'disableGroupedAction', hidden : true }
        ],
        form: [
            { path: 'alertConfigurationName', tKey: 'alert', type: 'readOnly' },
            { path: 'dataPeriod', tKey: 'period', type: 'readOnly' },
            { path: 'mesh', tKey: 'for', type: 'readOnly' },
            {
                path: 'keyAndValues',
                tKey: 'indicator_plural',
                type: 'readOnly'
            },
            { path: 'description', type: 'richTextEditor', disabled: true },
            {
                path: 'reason',
                display: 'tName',
                required: true,
                subscriptions: {
                    onChange: (newValue, oldValue, {formInitialize, getObjectSuccessAction, module}) => {
                        if(!formInitialize && !getObjectSuccessAction && newValue) {
                            const detailsField =  module.viewMap.form.fields.find(field => field.id === 'e_details')
                            detailsField.setValue("")
                        }
                    }
                }
            },
            { path: 'details', type: 'textarea', required: true},
            {
                path: 'ticketsLines',
                tKey: 'ticketLines',
                type: 'dtObjects',
                fields: [
                    { path: 'numLigne', tKey: 'lineNumber', width: 30 },
                    { path: 'cashier', tKey: 'cashier', width: 60 },
                    { path: 'product', tKey: 'product', width: 250 },
                    { path: 'productVolume', tKey: 'volume', width: 50 },
                    { path: 'themeKey', tKey: 'theme', width: 300 },
                    { path: 'themeValue', tKey: 'value', width: 50 }
                ]
            },

            { path: 'files', tKey: 'file_plural' },
            { path: 'comments', tKey: 'comment_plural' },

            { path: 'filesRequired', hidden: true },
            { path: 'isManager', hidden: true },
            { path: 'alertConfigurationReasonsIds', hidden: true },
            { path: 'buttons', hidden: true }
        ]
    },
    actionSubscriptions: [
        {
            actionType: GET_OBJECT_SUCCESS,
            subscription: ({ store }) => {
                const state = store.getState()

                const filesRequired = _.get(state, 'edit.object.data.filesRequired')

                store.dispatch(changeFieldRequired('e_files', filesRequired))
                // those are the ids of the reasons allowed for this alert
                const alertConfigurationReasonsIds = _.get(
                    state,
                    'edit.object.data.alertConfigurationReasonsIds',
                    []
                )

                store.dispatch(
                    setFieldListOptions(
                        'e_reason',
                        alertConfigurationReasonsIds
                    )
                )

                const userIsManager = _.get(state, 'edit.object.data.isManager', false)

                const ticketLines = _.get(
                    state,
                    'edit.object.data.ticketsLines',
                    []
                )

                const ticketLinesIsEmpty = ticketLines.length === 0
                store.dispatch(setFieldVisibility('e_ticketsLines', !ticketLinesIsEmpty))
                store.dispatch(setFieldEdition('e_reason', userIsManager))
                store.dispatch(setFieldEdition('e_details', userIsManager))
                store.dispatch(changeFieldRequired('e_reason', userIsManager))
                store.dispatch(changeFieldRequired('e_details', userIsManager))
            }
        }
    ],
    filters: [
        {
            object: 'WorkflowFilter',
            path: 'workflowFilter',
            tKey: 'status',
            display: 'tKey',
            translate: true,
            clearable: true,
            client: true
        },
        {
            path: 'period',
            type: 'dateRange',
            client: true,
            default: [twoMonthEarlierISO(), todayISO()]
        }
    ]
}
